import { createRoute, lazyRouteComponent } from '@tanstack/react-router';
import { rootRoute } from './__root';
import {
  columnPreferenceQueryOptions,
  emailAccountKeys,
} from '@/features/email-accounts/queries';
import { emailAccountsSearchSchema } from '@/features/email-accounts/search-schema';
import { searchToListParams } from '@/features/email-accounts/filters/filter-helpers';
import { EmailAccountsPendingState } from '@/features/email-accounts/EmailAccountsPendingState';
import { listAccounts } from '@/lib/email-accounts';
import { requireAuth } from '@/lib/route-guards';
import { primeListQuery } from '@/lib/route-loader';

export const emailAccountsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/email-accounts',
  validateSearch: emailAccountsSearchSchema,
  beforeLoad: requireAuth,
  // Echo every relevant search param into `loaderDeps` so the loader re-runs
  // when the user navigates between filter/page variants. TanStack Router
  // memoizes the loader by the structural identity of `loaderDeps`.
  loaderDeps: ({ search }) => ({
    params: searchToListParams(search),
  }),
  // Prime the email-accounts list cache before the component mounts. The
  // page's `useQuery` then resolves from cache and skips the loading state
  // when the user is authenticated. We swallow loader errors here — the
  // component's `useQuery` handles error/empty/loading states explicitly,
  // and we don't want a fetch failure to short-circuit the route render.
  //
  // The list prime only *blocks* (awaits) on a genuinely cold cache. Once any
  // page of this list has been fetched, `loaderDeps` changing again (a new
  // filter/sort/page combo) must not re-trigger the full-page
  // `pendingComponent` — `EmailAccountsPage`'s own `isFetching` + confined
  // `TableRefetchOverlay` owns that transition. See `primeListQuery`.
  loader: ({ context: { queryClient }, deps: { params } }) => {
    // Prime the column-preference query alongside the list so the table renders
    // its persisted column set on first paint (no defaults → saved-set flash).
    // A failed prime must not break the route — the table falls back to the
    // registry defaults via `normalizeColumnOrder(null)`.
    void queryClient
      .ensureQueryData(columnPreferenceQueryOptions('email-accounts'))
      .catch(() => undefined);
    return primeListQuery(queryClient, emailAccountKeys.lists(), {
      queryKey: emailAccountKeys.list(params),
      queryFn: () => listAccounts(params),
    });
  },
  // No debounce, no artificial display floor — visible for exactly as long as
  // the fetch takes (matches the `email-accounts.$id` route precedent).
  pendingComponent: EmailAccountsPendingState,
  pendingMs: 0,
  pendingMinMs: 0,
  component: lazyRouteComponent(
    () => import('@/features/email-accounts/EmailAccountsPage'),
    'EmailAccountsPage',
  ),
});
